import React, { Suspense, lazy, useEffect, useState } from "react"
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router-dom"
import { useAuth } from "../contexts/authContext"
import CrmProvider from "../contexts/crmContext"
import DataProvider from "../contexts/dataContext"
import sharedRoutes from "./SharedRoutes"
import Accounts from "../pages/Accounts"
import AdConversions from "../pages/AdConversions"
import ApiForm from "../pages/ApiIntegration/ApiForm"
import ApiIntegration from "../pages/ApiIntegration"
import ApiToken from "../pages/ApiIntegration/ApiToken"
import CallLog from "../pages/CallLog"
import ChatBotPage from "../pages/ChatBots/Elements/ChatBotPage"
import ChatBots from "../pages/ChatBots"
import ChatIntegration from "../pages/ChatIntegration"
import Content from "../pages/layouts/Content"
import Loading from "../pages/layouts/Loading"
import Crm from "../pages/Crm"
import Coupons from "../pages/Coupons"
import Dashboard from "../pages/Dashboard"
import DashboardChat from "../pages/Dashboard/DashboardChat"
import DashboardKanban from "../pages/Dashboard/DashboardKanban"
import DashboardMeta from "../pages/Dashboard/DashboardMeta"
import DashboardPartner from "../pages/Dashboard/DashboardPartner"
import DuplicateQueue from "../pages/Dashboard/DuplicateQueue"
import GoogleOAuthCallback from "../pages/GoogleIntegration/GoogleOAuthCallback"
import GoogleIntegration from "../pages/GoogleIntegration"
import ImportData from "../pages/ImportData"
import ImportError from "../pages/ImportError"
import ImportLog from "../pages/ImportLog"
import Integration from "../pages/Integration"
import ManagementLogChat from "../pages/ManagementLog/ManagementLogChat"
import ManagementLogCrm from "../pages/ManagementLog/ManagementLogCrm"
import ManagementLogTrash from "../pages/ManagementLog/ManagementLogTrash"
import MessengerIntegration from "../pages/MessengerIntegration"
import InstagramDirectIntegration from "../pages/InstagramDirectIntegration"
import InstagramOAuthCallback from "../pages/InstagramDirectIntegration/InstagramOAuthCallback"
import MetaIntegration from "../pages/MetaIntegration"
import MyAccount from "../pages/MyAccount"
import Notes from "../pages/Notes"
import Announcements from "../pages/Announcements"
import OutboundCampaign from "../pages/OutboundCampaign"
import OutboundCampaignDetails from "../pages/OutboundCampaignDetails"
import OutboundCampaignList from "../pages/OutboundCampaignList"
import Plans from "../pages/Plans"
import PlatformUpdates from "../pages/PlatformUpdates"
import Profile from "../pages/Profile"
import Redirect from "../pages/Redirect"
import Report from "../pages/Report"
import ReportDataChat from "../pages/ReportDataChat"
import ReportDataLead from "../pages/ReportDataLead"
import ReportDataNote from "../pages/ReportDataNote"
import ReportDataTask from "../pages/ReportDataTask"
import ReportLog from "../pages/ReportLog"
import Roles from "../pages/Roles"
import SelectAccount from "../pages/SelectAccount"
import SelectFunnel from "../pages/Crm/SelectFunnel"
import Settings from "../pages/Settings"
import Tasks from "../pages/Tasks"
import Teams from "../pages/Teams"
import Transactions from "../pages/Transactions"
import Trash from "../pages/Trash"
import TrashLead from "../pages/Trash/TrashLead"
import Users from "../pages/Users"
import Webhook from "../pages/Webhook"
import WhatsAppIntegration from "../pages/WhatsAppIntegration"
import WhatsAppApiIntegration from "../pages/WhatsAppApiIntegration"
import ZaiaIntegration from "../pages/ZaiaIntegration"
// import CreditCardForm from "../pages/Payment/CreditCardForm"

import AgentBuilder from "../pages/AgentBuilder"
import WhatsAppInstances from "../pages/WhatsAppInstances"
import { getRedirectFromSearch } from "../utils/authRedirect"

const Chat = lazy(() => import("../pages/Chat"))
const EmailCenter = lazy(() => import("../pages/EmailCenter"))
const CampaignCenter = lazy(() => import("../pages/EmailCenter/CampaignCenter"))
const SuppressionCenter = lazy(() => import("../pages/EmailCenter/SuppressionCenter"))
const DeliveryCenter = lazy(() => import("../pages/EmailCenter/DeliveryCenter"))
const EmailSettingsCenter = lazy(() => import("../pages/EmailCenter/EmailSettingsCenter"))

const AuthenticatedLoginRedirect = () => {
    const location = useLocation()
    return <Navigate to={getRedirectFromSearch(location.search)} replace />
}

const OtherRoutes = () => {
    const [defaultRoute, setDefaultRoute] = useState("/")
    const { account, verifyIfModuleIsActive, showAllOptions } = useAuth()

    useEffect(() => {
        setDefaultRoute(showAllOptions ? "/" : "/dashboard-partner")
    }, [account, showAllOptions])

    const listRoutes = [
        {
            path: "/:account_id/dashboard-partner",
            element: <Content namePermission="manage_accounts" title="Dashboard do Parceiro"><DashboardPartner /></Content>,
        },
        {
            path: "/:account_id/profile",
            element: <Content title="Perfil"><Profile /></Content>,
        },
        {
            path: "/:account_id/myaccount",
            element: <Content namePermission="finance" title="Minha Conta"><MyAccount /></Content>,
        },
        {
            path: "/:account_id/platform-updates",
            element: <Content title="Novidades"><PlatformUpdates /></Content>,
        },
        {
            path: "/:account_id/announcements",
            element: <Content title="Comunicados"><Announcements /></Content>,
        },
        {
            path: "/:account_id/reports/leads",
            element: <Content namePermission="reports" title="Relatório de Leads"><ReportDataLead /></Content>,
        },
        {
            path: "/:account_id/reports/chats",
            element: <Content namePermission="reports" title="Relatório do Chat"><ReportDataChat /></Content>,
        },
        {
            path: "/:account_id/reports/notes",
            element: <Content namePermission="reports" title="Relatório de Anotações"><ReportDataNote /></Content>,
        },
        {
            path: "/:account_id/reports/tasks",
            element: <Content namePermission="reports" title="Relatório de Tarefas"><ReportDataTask /></Content>,
        },
        {
            path: "/:account_id/reports/log",
            element: <Content namePermission="reports" title="Histórico de Exportações"><ReportLog /></Content>,
        },
        {
            path: "/:account_id/reports",
            element: <Content namePermission="reports" title="Relatórios"><Report /></Content>,
        },
        {
            path: "/:account_id/users",
            element: <Content namePermission="manage_users" title="Gerenciar Usuários"><Users /></Content>,
        },
        {
            path: "/:account_id/accounts",
            element: <Content namePermission="manage_accounts" title="Gerenciar Contas"><Accounts /></Content>,
        },
        {
            path: "/instagram/oauth/callback",
            element: <InstagramOAuthCallback />,
        },
    ]

    if (showAllOptions) {
        listRoutes.push(
            {
                path: "/:account_id/kanban/:funnel_id",
                element: <Content namePermission="crm" title="Kanban"><CrmProvider><Crm /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/kanban",
                element: <Content namePermission="crm" title="Kanban"><CrmProvider><SelectFunnel /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/dashboard",
                element: <Content namePermission="dashboard" title="Dashboard"><Dashboard /></Content>,
            },
            {
                path: "/:account_id/dashboard-kanban",
                element: <Content namePermission="dashboard" title="Dashboard Kanban"><DashboardKanban /></Content>,
            },
            {
                path: "/:account_id/crm/duplicates",
                element: <Content namePermission="duplicates" title="Fila de Duplicidades"><CrmProvider><DuplicateQueue /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/duplicates/review",
                element: <Content namePermission="duplicates" title="Fila de Duplicidades"><CrmProvider><DuplicateQueue /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/dashboard-meta",
                element: <Content namePermission="dashboard" title="Dashboard Meta"><DashboardMeta /></Content>,
            },
            {
                path: "/:account_id/dashboard-chat",
                element: <Content namePermission="dashboard" title="Dashboard Chat"><DashboardChat /></Content>,
            },
            {
                path: "/:account_id/calls/log",
                element: <Content namePermission="call_log" title="Registro de Chamadas"><CrmProvider><CallLog /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/tasks/log",
                element: <Content namePermission="tasks" title="Lista de Tarefas"><CrmProvider><Tasks /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/notes/log",
                element: <Content namePermission="notes" title="Histórico de anotações"><CrmProvider><Notes /></CrmProvider></Content>,
            },
            {
                path: "/:account_id/import",
                element: <Content namePermission="import" title="Importar Dados"><ImportData /></Content>,
            },
            {
                path: "/:account_id/import/log",
                element: <Content namePermission="import" title="Histórico de Importações"><ImportLog /></Content>,
            },
            {
                path: "/:account_id/import/:import_id/error",
                element: <Content namePermission="import" title="Erros na Importação"><ImportError /></Content>,
            },
            {
                path: "/:account_id/management/chat/log",
                element: <Content namePermission="settings" alternativePermissions={["chat"]} title="Histórico de ações em massa"><ManagementLogChat /></Content>,
            },
            {
                path: "/:account_id/management/kanban/log",
                element: <Content namePermission="settings" title="Histórico de Gerenciamento do CRM"><ManagementLogCrm /></Content>,
            },
            {
                path: "/:account_id/roles",
                element: <Content namePermission="manage_roles" title="Gerenciar Cargos"><Roles /></Content>,
            },
            {
                path: "/:account_id/teams",
                element: <Content namePermission="manage_teams" title="Gerenciar Equipes"><Teams /></Content>,
            },
            {
                path: "/:account_id/settings",
                element: <Content namePermission="settings" title="Configurações da Conta"><Settings /></Content>,
            },
            {
                path: "/:account_id/integrations",
                element: <Content namePermission="integrations" title="Integrações"><Integration /></Content>,
            },
            {
                path: "/:account_id/integrations/meta",
                element: <Content namePermission="integrations" title="Integração Meta Ads"><MetaIntegration /></Content>,
            },
            {
                path: "/:account_id/integrations/google",
                element: <Content namePermission="integrations" title="Integração Google Ads"><GoogleIntegration /></Content>,
            },
            {
                path: "/google/oauth/callback",
                element: <GoogleOAuthCallback />,
            },
            {
                path: "/:account_id/integrations/api",
                element: <Content namePermission="integrations" title="Integração via API"><ApiIntegration /></Content>,
            },
            {
                path: "/:account_id/integrations/api/tokens",
                element: <Content namePermission="integrations" title="Integração via API"><ApiToken /></Content>,
            },
            {
                path: "/:account_id/integrations/forms",
                element: <Content namePermission="integrations" title="Integração via API"><ApiForm /></Content>,
            },
            {
                path: "/:account_id/integrations/ads-conversions",
                element: <Content namePermission="integrations" title="Conversões"><AdConversions /></Content>,
            },
            {
                path: "/:account_id/integrations/webhook",
                element: <Content namePermission="integrations" title="Webhooks"><Webhook /></Content>,
            },
            {
                path: "/:account_id/trashs",
                element: <Content namePermission="settings" title="Lixeira"><Trash /></Content>,
            },
            {
                path: "/:account_id/trashs/leads",
                element: <Content namePermission="settings" title="Leads na Lixeira"><TrashLead /></Content>,
            },
            {
                path: "/:account_id/trashs/management",
                element: <Content namePermission="settings" title="Histórico de Restaurações"><ManagementLogTrash /></Content>,
            },
            {
                path: "/:account_id/plans",
                element: <Content namePermission="manage_plans" adminOnly={true} title="Gerenciar Planos"><Plans /></Content>,
            },
            {
                path: "/:account_id/transactions",
                element: <Content namePermission="manage_finance" adminOnly={true} title="Financeiro Interno"><Transactions /></Content>,
            },
            {
                path: "/:account_id/coupons",
                element: <Content namePermission="manage_finance" alternativePermissions={["manage_plans"]} adminOnly={true} title="Cupons de Desconto"><Coupons /></Content>,
            },
            {
                path: "/:account_id/email-center",
                element: <Content namePermission="manage_email_templates" adminOnly={true} title="Central de E-mails"><Suspense fallback={<Loading />}><EmailCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/email-center/campaigns",
                element: <Content namePermission="manage_email_campaigns" adminOnly={true} title="Comunicados"><Suspense fallback={<Loading />}><CampaignCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/email-center/campaigns/:campaign_id",
                element: <Content namePermission="manage_email_campaigns" adminOnly={true} title="Comunicados"><Suspense fallback={<Loading />}><CampaignCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/email-center/suppressions",
                element: <Content namePermission="manage_email_campaigns" adminOnly={true} title="Supressões de E-mail"><Suspense fallback={<Loading />}><SuppressionCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/email-center/deliveries",
                element: <Content namePermission="manage_email_campaigns" adminOnly={true} title="Entregas de E-mail"><Suspense fallback={<Loading />}><DeliveryCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/email-center/settings",
                element: <Content namePermission="manage_email_campaigns" adminOnly={true} title="Configurações de E-mail"><Suspense fallback={<Loading />}><EmailSettingsCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/email-center/:template_id",
                element: <Content namePermission="manage_email_templates" adminOnly={true} title="Central de E-mails"><Suspense fallback={<Loading />}><EmailCenter /></Suspense></Content>,
            },
            {
                path: "/:account_id/whatsapp-instances",
                element: <Content namePermission="manage_whatsapp_instances" adminOnly={true} title="Gerenciar Instâncias WhatsApp"><WhatsAppInstances /></Content>,
            },
            {
                path: "/:account_id/*",
                element: <Content namePermission="crm" title="Kanban"><CrmProvider><SelectFunnel /></CrmProvider></Content>,
            },
        )

        if (verifyIfModuleIsActive("CHAT")) {
            const platforms = account?.chat?.platforms || []
            listRoutes.push(
                {
                    path: "/:account_id/integrations/chat",
                    element: <Content namePermission="integrations" title="Integração Chat"><ChatIntegration /></Content>,
                },
                {
                    path: "/:account_id/integrations/chat/chatbots",
                    element: <Content namePermission="integrations" title="ChatBots IA"><ChatBots /></Content>,
                },
                {
                    path: "/:account_id/integrations/chat/agent-builder",
                    element: <Content namePermission="integrations" title="Agents IA"><AgentBuilder /></Content>,
                },
                {
                    path: "/:account_id/integrations/chat/chatbots/:flow_id/preview",
                    element: <Content namePermission="integrations" title="Fluxo do ChatBot"><ChatBotPage /></Content>,
                },
                {
                    path: "/:account_id/chat/:chat_id?",
                    element: <Content namePermission="chat" title="Chat"><CrmProvider><Suspense fallback={<Loading />}><Chat /></Suspense></CrmProvider></Content>,
                }
            )

            if (platforms.includes("whatsapp_api")) {
                listRoutes.push(
                    {
                        path: "/:account_id/integrations/chat/whatsapp-api",
                        element: <Content namePermission="whatsapp_inboxes" title="Integração WhatsApp API"><WhatsAppApiIntegration /></Content>,
                    },
                    {
                        path: "/:account_id/integrations/chat/outbound-campaign",
                        element: <Content namePermission="whatsapp_inboxes" title="Campanhas WhatsApp API"><OutboundCampaign /></Content>,
                    },
                    {
                        path: "/:account_id/integrations/chat/outbound-campaign/list",
                        element: <Content namePermission="whatsapp_inboxes" title="Histórico de Campanhas"><OutboundCampaignList /></Content>,
                    },
                    {
                        path: "/:account_id/integrations/chat/outbound-campaign/:campaign_id",
                        element: <Content namePermission="whatsapp_inboxes" title="Detalhes da Campanha"><OutboundCampaignDetails /></Content>,
                    }
                )
            }
            if (platforms.includes("whatsapp")) {
                listRoutes.push(
                    {
                        path: "/:account_id/integrations/chat/whatsapp",
                        element: <Content namePermission="whatsapp_inboxes" title="Integração WhatsApp Lite"><WhatsAppIntegration /></Content>,
                    }
                )
            }
            if (platforms.includes("messenger")) {
                listRoutes.push(
                    {
                        path: "/:account_id/integrations/chat/messenger",
                        element: <Content namePermission="integrations" title="Integração Messenger"><MessengerIntegration /></Content>,
                    }
                )
            }
            if (platforms.includes("direct")) {
                listRoutes.push(
                    {
                        path: "/:account_id/integrations/chat/instagram-direct",
                        element: <Content namePermission="integrations" title="Integração Instagram Direct"><InstagramDirectIntegration /></Content>,
                    }
                )
            }
            listRoutes.push(
                {
                    path: "/:account_id/integrations/chat/zaia",
                    element: <Content namePermission="integrations" title="Integração Zaia"><ZaiaIntegration /></Content>,
                }
            )
        }
    }

    // listRoutes.push({
    //     path: "/:account_id/start-trial",
    //     element: <Content namePermission="is_admin" title="Configurar Teste Grátis"><CreditCardForm /></Content>,
    // })

    listRoutes.push(...sharedRoutes)

    listRoutes.push(
        {
            path: "/login",
            element: <AuthenticatedLoginRedirect />,
        },
        {
            path: "/",
            element: <Content resetConfig={true}><SelectAccount /></Content>,
        },
        {
            path: "/:account_id/*",
            element: <Redirect to={defaultRoute} />,
        },
        {
            path: "*",
            element: <Navigate to="/" />,
        }
    )

    const router = createBrowserRouter(listRoutes)

    return (
        <DataProvider>
            <RouterProvider router={router} />
        </DataProvider>
    )
}

export default OtherRoutes
